import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  VStack,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  Box,
  Badge,
  Flex,
  Icon,
  Divider,
} from "@chakra-ui/react";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaChartBar,
} from "react-icons/fa";
import React from "react";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../../utils/themeUtils";
import { IconWrapper } from "../../../components/IconWrapper";
import Sidebar from '../../../components/Sidebar';
import { FiX, FiArrowLeft } from 'react-icons/fi';
import { Chart } from "react-google-charts";

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  permissions?: "Admin" | "Editor" | "Viewer";
};

export type Metric = {
  id: number;
  metric: string;
  target: string;
  actual: string;
  status: string;
};

interface KPISummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUser: User | null;
  userMetrics: { [userId: string]: Metric[] };
  bg: string;
  color: string;
  fullPage?: boolean;
}

const KPISummaryModal: React.FC<KPISummaryModalProps> = ({
  isOpen,
  onClose,
  selectedUser,
  userMetrics,
  bg,
  color,
  fullPage = false,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const metrics = selectedUser ? userMetrics[selectedUser.id] || [] : [];
  const totalMetrics = metrics.length;
  const completedMetrics = metrics.filter((m) => m.status === "✅").length;
  const inProgressMetrics = metrics.filter((m) => m.status === "⚠️").length;

  if (fullPage) {
    return (
      <div className={`flex min-h-[400px] mt-0 ${isDark ? 'bg-[#0B0B0F]' : 'bg-gray-50'}`}>
     
        <div className="flex-1 flex flex-col items-center justify-center p-6 relative mt-4 mb-0">
          <div className={`w-full max-w-3xl rounded-xl p-6 flex flex-col justify-center mt-0 shadow-md ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white border border-gray-200'}`} style={{ marginTop: '-32px' }}>
            <div className="flex flex-col items-center w-full">
              {/* Title Row */}
              <div className="flex items-center justify-between w-full max-w-xl mb-6">
                <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  KPI Summary
                </div>
                <div className="flex items-center gap-3">
                  <div className={`text-lg font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {selectedUser?.firstName} {selectedUser?.lastName}
                  </div>
                  <button
                    onClick={onClose}
                    className={`flex items-center justify-center rounded-full w-8 h-8 shadow-lg transition-all duration-200 hover:scale-105 ${isDark ? 'text-white bg-[#B59143] hover:bg-[#A7843D]' : 'text-white bg-[#B59143] hover:bg-[#A7843D]'}`}
                    aria-label="Back"
                  >
                    <IconWrapper icon={FiArrowLeft} size={20} />
                  </button>
                </div>
              </div>

              {/* Chart Section */}
              <div className={`flex flex-col items-center rounded-lg p-6  w-full max-w-xl mb-6 ${isDark ? 'bg-[#14161B]' : 'bg-white'}`}>
                <div className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-700'}`}>Progress Overview</div>
                <div className="flex justify-center items-center mb-3">
                  <Chart
                    chartType="PieChart"
                    width="280px"
                    height="180px"
                    data={[
                      ["Status", "Count"],
                      ["Completed", completedMetrics],
                      ["In Progress", inProgressMetrics],
                    ]}
                    options={{
                      pieHole: 0.6,
                      is3D: true,
                      slices: {
                        0: { color: '#4ade80' },
                        1: { color: '#fde047' },
                      },
                      legend: 'none',
                      pieSliceText: 'percentage',
                      pieSliceBorderColor: '#fff',
                      chartArea: { width: '90%', height: '90%' },
                      backgroundColor: 'transparent',
                    }}
                  />
                </div>
                {/* Legend */}
                <div className="flex justify-center gap-4 mt-2">
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full shadow-sm ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-gray-50 border border-gray-100'}`}>
                    <span className="inline-block w-4 h-4 rounded-full" style={{ background: '#4ade80' }}></span>
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>Completed</span>
                    <span className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{completedMetrics}</span>
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full shadow-sm ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-gray-50 border border-gray-100'}`}>
                    <span className="inline-block w-4 h-4 rounded-full" style={{ background: '#fde047' }}></span>
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>In Progress</span>
                    <span className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{inProgressMetrics}</span>
                  </div>
                </div>
              </div>

              {/* Tracking Text */}
              <div className="w-full max-w-xl">
                <div className={`text-center text-base font-medium rounded-lg py-3 px-4 ${isDark ? 'text-gray-300 bg-[#14161B] border border-[#2E2F33]' : 'text-gray-600 bg-gray-50 border border-gray-100'}`}>
                  {totalMetrics === 0
                    ? "No KPI metrics assigned yet."
                    : `Tracking ${totalMetrics} metrics for ${selectedUser?.firstName}`}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent bg={isDark ? '#1A1C1F' : bg} color={isDark ? 'white' : color} p={6} borderRadius="xl" boxShadow="2xl" border={isDark ? '1px solid #2E2F33' : undefined}>
        <ModalHeader textAlign="center" fontSize="xl" fontWeight="bold" mb={4}>
          KPI Summary - {selectedUser?.firstName} {selectedUser?.lastName}
        </ModalHeader>
        <ModalCloseButton size="md" />

        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* KPI Stats Section */}
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              {/* Completed Metrics */}
              <GridItem p={4} borderRadius="lg" bg={color} textAlign="center" boxShadow="md" transition="all 0.3s" _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}>
                <Flex direction="column" align="center">
                  <IconWrapper
                    icon={FaCheckCircle}
                    size={24}
                    className="text-green-400 mb-3"
                  />
                  <Stat>
                    <StatLabel fontSize="md" color={bg} fontWeight="medium">
                      Completed
                    </StatLabel>
                    <StatNumber fontSize="2xl" color={bg} fontWeight="bold" my={1}>
                      {completedMetrics}
                    </StatNumber>
                    <Badge fontSize="sm" bg="#4ade80" color="white" px={3} py={0.5} borderRadius="full">
                      ✅ Completed
                    </Badge>
                  </Stat>
                </Flex>
              </GridItem>

              {/* In Progress Metrics */}
              <GridItem p={4} borderRadius="lg" bg={color} textAlign="center" boxShadow="md" transition="all 0.3s" _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}>
                <Flex direction="column" align="center">
                  <IconWrapper
                    icon={FaExclamationTriangle}
                    size={24}
                    className="text-yellow-500 mb-3"
                  />
                  <Stat>
                    <StatLabel fontSize="md" color={bg} fontWeight="medium">
                      In Progress
                    </StatLabel>
                    <StatNumber fontSize="2xl" color={bg} fontWeight="bold" my={1}>
                      {inProgressMetrics}
                    </StatNumber>
                    <Badge fontSize="sm" bg="#fde047" color={isDark ? '#111827' : 'gray.700'} px={3} py={0.5} borderRadius="full">
                      ⚠️ In Progress
                    </Badge>
                  </Stat>
                </Flex>
              </GridItem>
            </Grid>

            <Divider opacity="0.1" />

            {/* KPI Summary Text */}
            <Box textAlign="center" fontSize="md" color={isDark ? 'gray.300' : 'gray.500'} bg={isDark ? '#14161B' : 'gray.50'} border={isDark ? '1px solid #2E2F33' : undefined} p={3} borderRadius="lg">
              {totalMetrics === 0
                ? "No KPI metrics assigned yet."
                : `Tracking ${totalMetrics} metrics for ${selectedUser?.firstName}.`}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default KPISummaryModal;
